import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import OrderbookModal from '../components/OrderbookModal';
import { BASE_URL, breadcrumbLD } from '../seo/routeMetadata';
import { MARKET_CATEGORIES } from '../seo/seoContent';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(iso);
  }
};

const MarketDetailPage = () => {
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();

  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);

  const catMeta = MARKET_CATEGORIES[category];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/api/markets/by-slug/${category}/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(res.status === 404 ? 'not-found' : 'unknown');
          return;
        }
        const data = await res.json();
        if (!cancelled) setMarket(data);
      } catch (e) {
        if (!cancelled) setError('network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-gray-400">Loading market…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error === 'not-found' || !market) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Market not found</h1>
          <p className="text-gray-400 mb-6">This market may have been resolved, or the URL is incorrect.</p>
          <Link to="/markets" className="text-[#3B82F6] hover:underline">← Back to all markets</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const url = `${BASE_URL}/markets/${category}/${slug}`;
  const title = market.title;
  const yesPrice = Math.round(market.yes_price ?? 50);
  const noPrice = 100 - yesPrice;

  // Keep SEO <title> under Google's 60-char cutoff.
  //   " | AMP Predict" suffix = 14 chars → question must fit in 46 chars.
  //   If longer, truncate at a word boundary and add an ellipsis.
  const truncate = (s, max) => {
    if (!s || s.length <= max) return s;
    const cut = s.slice(0, max - 1);
    const last = cut.lastIndexOf(' ');
    return (last > max * 0.6 ? cut.slice(0, last) : cut).trimEnd() + '…';
  };
  const seoTitle = `${truncate(title, 46)} | AMP Predict`;

  // Unique per-market description. Combining category, YES price, and
  // closing date makes every market's description distinct — even when
  // multiple markets share a title or lack a custom description.
  const closesFmt = market.closes_at ? fmtDate(market.closes_at) : 'a future date';
  const catName = catMeta ? catMeta.h1.replace(' Prediction Markets', '') : (market.category || 'events');
  const baseCopy = market.description
    ? String(market.description).slice(0, 140).trimEnd()
    : `Practice forecasting "${truncate(title, 90)}"`;
  const description = `${baseCopy}. YES trades at ${yesPrice}¢ · closes ${closesFmt} · ${catName} category on AMP Predict — virtual TK¢ tokens, no real money.`.slice(0, 300);

  const eventLd = {
    '@type': 'Event',
    name: title,
    description,
    url,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    startDate: market.opens_at || market.created_at,
    endDate: market.closes_at,
    location: { '@type': 'VirtualLocation', url },
    organizer: { '@type': 'Organization', name: 'AMP Predict', url: BASE_URL },
    about: catMeta ? catMeta.h1 : (market.category || 'Prediction markets'),
  };
  const productLd = {
    '@type': 'FinancialProduct',
    name: `YES/NO contract — ${title}`,
    description,
    url,
    provider: { '@type': 'Organization', name: 'AMP Predict', url: BASE_URL },
    category: catMeta ? catMeta.h1 : market.category,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: (yesPrice / 100).toFixed(2),
      availability: (market.status === 'OPEN')
        ? 'https://schema.org/InStock'
        : 'https://schema.org/Discontinued',
      url,
    },
  };
  const crumbLd = breadcrumbLD([
    { name: 'Home', path: '/' },
    { name: 'Markets', path: '/markets' },
    { name: catMeta ? catMeta.h1 : 'Category', path: `/markets/${category}` },
    { name: title, path: `/markets/${category}/${slug}` },
  ]);

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title={seoTitle}
        description={description}
        url={url}
        jsonLd={[eventLd, productLd, crumbLd]}
      />
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Unified market header — breadcrumb, title, and status meta all
            in one visual block. Removes the separate "Back" button (the
            breadcrumb serves that role). */}
        <header className="mb-8 pb-6 border-b border-[#1e2a45]">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-gray-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-gray-600">›</span>
            <Link to="/markets" className="hover:text-white">Markets</Link>
            <span className="mx-2 text-gray-600">›</span>
            <Link to={`/markets/${category}`} className="hover:text-white">
              {catMeta ? catMeta.h1.replace(' Prediction Markets','') : category}
            </Link>
            <span className="mx-2 text-gray-600">›</span>
            <span className="text-gray-400 truncate inline-block max-w-[200px] sm:max-w-none align-bottom">{title}</span>
          </nav>

          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3" data-testid="market-detail-h1">{title}</h1>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className={`font-semibold px-2 py-0.5 rounded-full border ${market.status === 'OPEN' ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'}`}>
              {market.status || 'OPEN'}
            </span>
            <span className="text-gray-400">
              {catMeta ? catMeta.h1.replace(' Prediction Markets','') : market.category}
              {market.subcategory && <span className="text-gray-500"> · {String(market.subcategory).replace(/_/g,' ')}</span>}
            </span>
            <span aria-hidden="true" className="text-gray-600">·</span>
            <span className="text-gray-500">Closes <time dateTime={market.closes_at}>{fmtDate(market.closes_at)}</time></span>
          </div>
        </header>

        {/* Price panel */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-5 text-center">
            <div className="text-xs uppercase tracking-wider text-green-400 mb-1">YES</div>
            <div className="text-4xl font-bold text-white">{yesPrice}<span className="text-lg">¢</span></div>
            <div className="text-xs text-gray-500 mt-1">implied prob {yesPrice}%</div>
          </div>
          <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 text-center">
            <div className="text-xs uppercase tracking-wider text-red-400 mb-1">NO</div>
            <div className="text-4xl font-bold text-white">{noPrice}<span className="text-lg">¢</span></div>
            <div className="text-xs text-gray-500 mt-1">implied prob {noPrice}%</div>
          </div>
        </div>

        <button
          onClick={() => setTradeOpen(true)}
          data-testid="market-detail-trade-btn"
          className="w-full sm:w-auto bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-full font-semibold mb-10"
        >
          Open orderbook &amp; trade with virtual TK¢
        </button>

        {market.description && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-2">About this market</h2>
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{market.description}</p>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-3">Market details</h2>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-gray-500">Category</dt>
            <dd className="text-gray-300">{catMeta ? catMeta.h1.replace(' Prediction Markets','') : market.category}</dd>
            <dt className="text-gray-500">Status</dt>
            <dd className="text-gray-300">{market.status || 'OPEN'}</dd>
            <dt className="text-gray-500">Opens</dt>
            <dd className="text-gray-300">{fmtDate(market.opens_at || market.created_at)}</dd>
            <dt className="text-gray-500">Closes</dt>
            <dd className="text-gray-300">{fmtDate(market.closes_at)}</dd>
            <dt className="text-gray-500">Implied YES probability</dt>
            <dd className="text-gray-300">{yesPrice}%</dd>
          </dl>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-2">How trading works on AMP Predict</h2>
          <p className="text-gray-300 leading-relaxed">
            AMP Predict is a mock prediction market. All positions are denominated in virtual TK¢ tokens — no real money is wagered. A YES contract pays 100 TK¢ if the event resolves YES and 0 TK¢ otherwise. Learn more in our{' '}
            <Link to="/learn/how-yes-no-event-contracts-work" className="text-[#3B82F6] hover:underline">YES/NO contracts guide</Link>.
          </p>
        </section>

        <div className="pt-8 border-t border-[#1e2a45]">
          <h3 className="text-lg font-semibold text-white mb-4">Explore more</h3>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to={`/markets/${category}`} className="text-[#3B82F6] hover:underline">All {catMeta ? catMeta.h1.replace(' Prediction Markets','') : category} markets →</Link>
            <Link to="/learn" className="text-[#3B82F6] hover:underline">Learn hub →</Link>
            <Link to="/how-it-works" className="text-[#3B82F6] hover:underline">How AMP Predict works →</Link>
          </div>
        </div>
      </main>
      <Footer />

      {/* Trading modal reuses the existing orderbook flow */}
      <OrderbookModal
        isOpen={tradeOpen}
        onClose={() => setTradeOpen(false)}
        market={market}
        isSubscribed={isSubscribed}
        onShowSubscribe={() => navigate('/subscribe')}
      />
    </div>
  );
};

export default MarketDetailPage;
